import { Component } from 'react';
import { sendRenderErrorBeacon } from '@/lib/renderErrorBeacon';

/**
 * Class-based React Error Boundary. Catches errors thrown during render of
 * any descendant component. Reports to /api/route-health/render-error so
 * the backend can surface them on the Route Health page — same idea as
 * Sentry but free + tenant-scoped + integrated with the existing beacon.
 *
 * Falls back to a friendly recovery UI ("Something broke on this page —
 * Try reloading / Go home") instead of the white-screen-of-death.
 */
export class RenderErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      sendRenderErrorBeacon({
        message: error?.message || String(error),
        stack: error?.stack || errorInfo?.componentStack || '',
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
    } catch (_) {}
  }

  reload = () => {
    try { window.location.reload(); } catch (_) {}
  };

  goHome = () => {
    try { window.location.href = '/'; } catch (_) {}
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="render-error-boundary"
        className="min-h-screen flex items-center justify-center p-6 bg-stone-50 dark:bg-stone-900"
      >
        <div className="max-w-lg w-full bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-2xl p-7 shadow-xl text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mb-3">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-lg font-bold font-outfit mb-1">Something broke on this page</h2>
          <p className="text-sm text-muted-foreground mb-4">
            We've reported it to your admin so it can be fixed. You can keep working from another page.
          </p>
          {this.state.error?.message && (
            <pre className="text-[11px] text-left bg-stone-100 dark:bg-stone-900 p-3 rounded-md mb-4 max-h-32 overflow-auto whitespace-pre-wrap font-mono text-rose-700 dark:text-rose-400">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.reload}
              data-testid="render-error-reload-btn"
              className="rounded-lg bg-orange-600 text-white px-4 py-2 text-sm font-semibold hover:bg-orange-700 transition"
            >
              Try reloading
            </button>
            <button
              type="button"
              onClick={this.goHome}
              data-testid="render-error-home-btn"
              className="rounded-lg border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 px-4 py-2 text-sm font-semibold hover:bg-stone-100 dark:hover:bg-stone-700 transition"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default RenderErrorBoundary;
